<template>
  <div class="ai-chat-panel">
    <div class="chat-header">
      <span class="title">AI 智能助手</span>
      <button class="close-btn" @click="handleClose">×</button>
    </div>
    
    <div class="chat-messages">
      <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.type === 'ai' ? 'ai-message' : 'user-message']">
        <div v-if="msg.type === 'ai'" class="avatar">🤖</div>
        
        <!-- Message Content Wrapper -->
        <div class="message-content-wrapper">
          <!-- Image Display -->
          <div v-if="msg.image" class="message-image">
            <img :src="msg.image" alt="Generated Image" @error="$event.target.style.display='none'" />
          </div>
          
          <!-- Text Bubble -->
          <div v-if="msg.lines && msg.lines.length > 0 && msg.lines[0]" class="content text-bubble">
            <div v-if="msg.type === 'ai'" class="markdown-body" v-html="renderMarkdown(msg.lines[0])"></div>
            <p v-else v-for="(line, i) in msg.lines" :key="i">{{ line }}</p>
          </div>
          
          <!-- Recommendations Component -->
          <RecommendationList 
            v-if="msg.recommendations && msg.recommendations.length > 0" 
            :recommendations="msg.recommendations" 
            @navigate="onNavigate" 
          />
          
          <!-- Facility List Component -->
          <FacilityList 
            v-if="msg.facilityList && msg.facilityList.length > 0" 
            :facilities="msg.facilityList" 
            @navigate="onNavigate" 
          />
        </div>

        <div v-if="msg.type === 'user'" class="avatar user-avatar">👤</div>
      </div>
    </div>

    <div class="chat-controls">
      <div v-if="isRecording" class="recording-overlay">
        <div class="recording-status" :class="{ 'cancel-state': isCanceling }">
           <div class="icon">{{ isCanceling ? '↩️' : '🎙️' }}</div>
           <div class="text">{{ isCanceling ? '松开手指 取消发送' : '正在听...' }}</div>
        </div>
        <div class="realtime-text" v-if="recognizedText">{{ recognizedText }}</div>
      </div>

      <button 
        class="voice-btn" 
        :class="{ 'recording': isRecording }"
        @mousedown="handlePressStart"
        @touchstart.prevent="handlePressStart"
      >
        <span class="icon">🎙️</span>
        {{ isRecording ? '松开 发送' : '按住 说话' }}
      </button>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import RecommendationList from './RecommendationList.vue';
import FacilityList from './FacilityList.vue';

export default {
  name: 'AIChatPanel',
  components: {
    RecommendationList,
    FacilityList
  },
  data() {
    return {
      conversation_id: '',
      messages: [
        {
          type: 'ai',
          lines: [
            'Hi~ 我是您的AI新朋友-小北。\n很高兴为您服务，您可以直接对我说出您的问题。'
          ]
        }
      ],
      md: new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true,
        breaks: true
      }),
      // Voice Recording State
      isRecording: false,
      isCanceling: false,
      startY: 0,
      recognizedText: '',
      // Baidu ASR Config
      baiduAsrConfig: {
        appid: '120568411', 
        appkey: 'tcmlB6nZwQaM8y0WXWpwn1u6',
        secretKey: 'FMeYLwB59JT1mQDFNaC6HvgN2W8A9AiI', // 请在此处填入百度云 Secret Key
        dev_pid: 15372, 
      },
      baiduToken: null,
      audioQueue: [],
      currentAudio: null,
      isPlayingAudio: false,
      audioContext: null,
      mediaStream: null,
      scriptProcessor: null,
      ws: null,
      audioSampleRate: 0,
      resampleRate: 16000,
      abortController: null,
    }
  },
  emits: ['close', 'navigate', 'speaking-start', 'speaking-stop'],
  methods: {
    // Voice Methods
    handlePressStart(e) {
      if (this.isRecording) return;
      
      this.startY = e.touches ? e.touches[0].clientY : e.clientY;
      this.isCanceling = false;
      this.isRecording = true; // Set state immediately for UI feedback and tracking
      
      // Add global listeners to window to handle swipe/drag outside button
      window.addEventListener('mousemove', this.handlePressMove);
      window.addEventListener('touchmove', this.handlePressMove, { passive: false });
      window.addEventListener('mouseup', this.handlePressEnd);
      window.addEventListener('touchend', this.handlePressEnd);
      
      this.startRecording();
    },
    handlePressMove(e) {
      if (!this.isRecording) return;
      
      const currentY = e.touches ? e.touches[0].clientY : e.clientY;
      // If moved up more than 50px
      if (this.startY - currentY > 50) {
        this.isCanceling = true;
      } else {
        this.isCanceling = false;
      }
    },
    handlePressEnd() {
      // Remove global listeners
      window.removeEventListener('mousemove', this.handlePressMove);
      window.removeEventListener('touchmove', this.handlePressMove);
      window.removeEventListener('mouseup', this.handlePressEnd);
      window.removeEventListener('touchend', this.handlePressEnd);

      if (this.isRecording) {
         this.stopRecording();
      }
    },
    async startRecording() {
      console.log('Starting recording...');
      this.stopSpeaking();
      // Removed early return check since we set isRecording=true in handlePressStart
      // if (this.isRecording) return; 
      
      try {
        console.log('Requesting microphone access...');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('Microphone access granted');
        
        // If user already released the button while we were waiting for mic
        if (!this.isRecording) {
            stream.getTracks().forEach(track => track.stop());
            return;
        }

        this.mediaStream = stream;
        
        // this.isRecording = true; // Already set in handlePressStart
        this.recognizedText = '';
        
        this.initWebSocket();
        
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContext();
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
        this.audioSampleRate = this.audioContext.sampleRate;
        console.log('AudioContext initialized, sampleRate:', this.audioSampleRate);
        
        const source = this.audioContext.createMediaStreamSource(stream);
        // 4096 buffer size
        this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);
        
        source.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.audioContext.destination);
        
        this.scriptProcessor.onaudioprocess = (e) => {
          if (!this.isRecording) return;
          if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
             // console.log('WebSocket not ready yet');
             return;
          }
          const inputBuffer = e.inputBuffer.getChannelData(0);
          const pcmData = this.transcode(inputBuffer);
          this.ws.send(pcmData);
        };
      } catch (error) {
        console.error('Recording failed:', error);
        alert('无法访问麦克风: ' + error.message);
        this.stopRecording();
      }
    },
    stopRecording() {
      console.log('Stopping recording...');
      const wasCanceling = this.isCanceling;
      this.isRecording = false;
      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }
      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
      }
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }
      if (this.ws) {
        if (this.ws.readyState === WebSocket.OPEN) {
          console.log('Sending FINISH frame');
          this.ws.send(JSON.stringify({ type: 'FINISH' }));
        }
        // Wait for final result
        setTimeout(() => {
          if (this.ws) {
            this.ws.close();
            this.ws = null;
          }
          
          if (wasCanceling) {
             console.log('Recording canceled by user');
             this.recognizedText = '';
          } else {
             if (this.recognizedText && this.recognizedText.trim()) {
                console.log('Sending recognized text:', this.recognizedText);
                this.handleUserQuestion(this.recognizedText);
             } else {
                console.log('No recognized text captured');
             }
          }
          // Reset state
          this.recognizedText = '';
          this.isCanceling = false;
        }, 800);
      } else {
          this.isCanceling = false;
      }
    },
    initWebSocket() {
      const sn = 'web-' + Date.now();
      const url = `wss://vop.baidu.com/realtime_asr?sn=${sn}`;
      console.log('Connecting to WebSocket:', url);
      this.ws = new WebSocket(url);
      this.ws.binaryType = 'arraybuffer';
      
      this.ws.onopen = () => {
        console.log('WebSocket Open');
        const startFrame = {
          type: 'START',
          data: {
            appid: Number(this.baiduAsrConfig.appid),
            appkey: this.baiduAsrConfig.appkey,
            dev_pid: this.baiduAsrConfig.dev_pid,
            format: 'pcm',
            sample: 16000,
            cuid: 'web-user'
          }
        };
        this.ws.send(JSON.stringify(startFrame));
      };
      
      this.ws.onmessage = (e) => {
        if (typeof e.data === 'string') {
          try {
            const res = JSON.parse(e.data);
            if (res.err_no === 0 && res.result) {
              this.recognizedText = res.result;
              console.log('Recognition update:', res.result);
            } else if (res.err_no !== 0) {
              console.warn('ASR Error:', res);
            }
          } catch (err) {
            console.error('JSON Parse Error:', err);
          }
        }
      };

      this.ws.onerror = (e) => {
          console.error('WebSocket Error:', e);
      };
      
      this.ws.onclose = () => {
          console.log('WebSocket Closed');
      };
    },
    transcode(audioData) {
      const inputSampleRate = this.audioSampleRate;
      const outputSampleRate = this.resampleRate;
      const compression = inputSampleRate / outputSampleRate;
      const length = audioData.length / compression;
      const result = new Int16Array(length);
      let index = 0, j = 0;
      while (index < length) {
        let offset = Math.floor(j);
        let nextOffset = Math.floor(j + compression);
        let accum = 0, count = 0;
        for (let i = offset; i < nextOffset && i < audioData.length; i++) {
          accum += audioData[i];
          count++;
        }
        let s = accum / count;
        s = Math.max(-1, Math.min(1, s));
        result[index] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        index++;
        j += compression;
      }
      return result.buffer;
    },

    onNavigate(data) {
      this.$emit('navigate', data);
      this.$emit('close');
    },
    renderMarkdown(text) {
        if (!text) return '';
        return this.md.render(text);
    },
    async callDifyApi(query) {
        // Cancel previous request if exists
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();

        // Add empty AI message
        const aiMsgIndex = this.messages.push({
            type: 'ai',
            lines: ['思考中...'],
            recommendations: []
        }) - 1;

        try {
            // Append system instruction for JSON output
            // const systemInstruction = `\n\n(System Note: Please respond with a JSON object containing two fields: "text" (your markdown response) and "recommendations" (an array of recommended items). If no recommendations, the array should be empty. Format: { "text": "...", "recommendations": [{"name": "Brand Name", "floor": "F1", "floor_id": 1, "x": 12961600, "y": 4861800, "logo": "url", "description": "short desc"}] })\nNote: If you know the specific map coordinates (x, y) and floor_id (groupID) of the location, please include them.`;
            
            const response = await fetch(`${window.APP_CONFIG.DIFY_API_URL}/chat-messages`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${window.APP_CONFIG.DIFY_API_KEY}`,
                    'Content-Type': 'application/json'
                },
                signal: this.abortController.signal,
                body: JSON.stringify({
                    inputs: {},
                    query: query,
                    response_mode: "blocking",
                    conversation_id: this.conversation_id,
                    user: "user-123"
                })
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            
            if (data.answer) {
                this.conversation_id = data.conversation_id;
                let answerText = data.answer;
                let recommendations = [];
                let facilityList = [];
                let image = '';

                // Attempt to parse the entire answer as JSON first (if the AI followed instructions perfectly)
                // Or find a JSON block
                let jsonParsed = false;
                
                // Try finding JSON block first as AI might wrap it in markdown code block
                // const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/;
                // const blockMatch = answerText.match(jsonBlockRegex);
                
                // if (blockMatch) {
                //     try {
                //         const parsed = JSON.parse(blockMatch[1]);
                //         if (parsed.text || parsed.recommendations) {
                //              answerText = parsed.text || '';
                //              recommendations = parsed.recommendations || [];
                //              jsonParsed = true;
                //         }
                //     } catch (e) {
                //         console.log('Failed to parse JSON block, trying fallback');
                //     }
                // }

                // If block parsing failed or didn't exist, try parsing raw text if it looks like JSON
                if (!jsonParsed && answerText.trim().startsWith('{')) {
                    try {
                        const parsed = JSON.parse(answerText);
                         if (parsed.text || parsed.recommendations || parsed.image || parsed.facilityList) {
                             answerText = parsed.text || '';
                             recommendations = parsed.recommendations || [];
                             facilityList = parsed.facilityList || [];
                             image = parsed.image || '';
                             jsonParsed = true;
                        }
                    } catch (e) {
                        console.log('Failed to parse raw JSON');
                    }
                }

                // Fallback: If AI didn't return the requested structure but appended the old format or just text
                if (!jsonParsed) {
                     // Check for the old appended array format just in case
                     const arrayRegex = /```json\s*(\[[\s\S]*?\])\s*```/;
                     const arrayMatch = answerText.match(arrayRegex);
                     if (arrayMatch) {
                         try {
                             recommendations = JSON.parse(arrayMatch[1]);
                             answerText = answerText.replace(arrayMatch[0], '').trim();
                         } catch(e) {
                            console.log('Failed to parse JSON array:', e);
                         }
                     }
                }
                
                // 判断image中是否携带域名，如果没有则补全
                if (image && !image.startsWith('http')) {
                    image = 'https://api.beiguo.juzhenweilai.com/' + image;
                }

                this.messages[aiMsgIndex].lines[0] = answerText;
                this.messages[aiMsgIndex].recommendations = recommendations;
                this.messages[aiMsgIndex].facilityList = facilityList;
                this.messages[aiMsgIndex].image = image;

                this.speakText(answerText);
            } else {
                 this.messages[aiMsgIndex].lines[0] = '抱歉，我没有理解您的问题。';
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Request aborted');
                // Remove the 'thinking' message if aborted
                this.messages.splice(aiMsgIndex, 1);
                return;
            }
            console.error('Dify API Error:', error);
            this.messages[aiMsgIndex].lines = ['抱歉，我遇到了一些问题，请稍后再试。'];
        } finally {
            this.abortController = null;
        }
    },
    handleExternalMessage(context) {
        const text = context;
        this.messages.push({
            type: 'user',
            lines: [text]
        });
        
        this.callDifyApi(text);
    },
    handleUserQuestion(text) {
        this.messages.push({
            type: 'user',
            lines: [text]
        });
        
        this.callDifyApi(text);
    },
    handleClose() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.stopSpeaking();
      this.$emit('close');
    },
    stopSpeaking() {
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }
      this.audioQueue = [];
      this.isPlayingAudio = false;

      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      this.$emit('speaking-stop');
    },
    async getBaiduToken() {
      if (this.baiduToken && Date.now() < this.baiduToken.expireTime) {
        return this.baiduToken.access_token;
      }
      
      if (!this.baiduAsrConfig.secretKey) {
        console.warn('Baidu Secret Key is missing. Fallback to browser TTS.');
        return null;
      }

      try {
        // Use proxy to avoid CORS
        // /baidu-api/oauth/2.0/token -> https://aip.baidubce.com/oauth/2.0/token
        const apiPrefix = window.APP_CONFIG.BAIDU_API_PREFIX || '/baidu-api';
        const response = await fetch(`${apiPrefix}/oauth/2.0/token?grant_type=client_credentials&client_id=${this.baiduAsrConfig.appkey}&client_secret=${this.baiduAsrConfig.secretKey}`, {
            method: 'POST'
        });
        const data = await response.json();
        if (data.access_token) {
          this.baiduToken = {
            access_token: data.access_token,
            expireTime: Date.now() + (data.expires_in * 1000) - 60000 // Buffer 1 min
          };
          return data.access_token;
        }
      } catch (e) {
        console.error('Failed to get Baidu Token:', e);
      }
      return null;
    },
    async speakText(text) {
      this.stopSpeaking();
      
      // Strip markdown/html for better speech
      const html = this.md.render(text);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      let plainText = tempDiv.textContent || tempDiv.innerText || '';
      plainText = plainText.trim();
      
      if (!plainText) return;

      const token = await this.getBaiduToken();
      if (!token) {
        this.browserSpeak(plainText);
        return;
      }

      // Split text into chunks by punctuation
      const chunks = plainText.match(/[^。！？；!?;\n]+[。！？；!?;\n]?/g) || [plainText];
      this.audioQueue = [...chunks];
      this.playNextAudio(token);
    },
    playNextAudio(token) {
        if (this.audioQueue.length === 0) {
            this.isPlayingAudio = false;
            this.$emit('speaking-stop');
            return;
        }
        
        const text = this.audioQueue.shift();
        if (!text.trim()) {
            this.playNextAudio(token);
            return;
        }

        const cuid = 'web-' + Date.now();
        // Use per=0 for Du Xiaomei (standard female)
        // Use proxy /baidu-tts -> https://tsn.baidu.com
        const ttsPrefix = window.APP_CONFIG.BAIDU_TTS_PREFIX || '/baidu-tts';
        const url = `${ttsPrefix}/text2audio?tex=${encodeURIComponent(text)}&tok=${token}&cuid=${cuid}&ctp=1&lan=zh&aue=3&per=0`;

        this.isPlayingAudio = true;
        this.$emit('speaking-start');
        this.currentAudio = new Audio(url);
        this.currentAudio.onended = () => {
            this.playNextAudio(token);
        };
        this.currentAudio.onerror = (e) => {
            console.error('Audio playback error', e);
            this.playNextAudio(token);
        };
        this.currentAudio.play().catch(e => {
            console.error('Play failed:', e);
            this.playNextAudio(token);
        });
    },
    browserSpeak(text) {
       if (!window.speechSynthesis) return;
       const utterance = new SpeechSynthesisUtterance(text);
       utterance.lang = 'zh-CN';
       utterance.onstart = () => this.$emit('speaking-start');
       utterance.onend = () => this.$emit('speaking-stop');
       utterance.onerror = () => this.$emit('speaking-stop');
       window.speechSynthesis.speak(utterance);
    }
  },
  beforeUnmount() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.stopRecording();
    this.stopSpeaking();
  },
  expose: ['handleExternalMessage', 'handleUserQuestion']
}
</script>

<style scoped>
/* ... existing styles ... */
.ai-chat-panel {
  position: absolute;
  top: 1vh;
  left: 8%; /* Center horizontally with 96% width */
  width: 84%;
  height: calc(100% - 2vh);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  z-index: 100;
  display: flex;
  flex-direction: column;
  border-radius: 1.5vh;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(0,0,0,0.1);
}

.chat-header {
  height: 5vh;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5vw;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  flex: 0 0 auto;
}

.title {
  font-size: 1rem;
  font-weight: bold;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.chat-messages {
  flex: 1;
  padding: 1.5vh 1.5vw;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.5vh;
}

.message {
  display: flex;
  gap: 1vh;
  max-width: 85%;
}

.ai-message .avatar {
  width: 3.5vh;
  height: 3.5vh;
  background: #d14d5e;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
  color: #fff;
  flex-shrink: 0;
}

.ai-message .content {
  background: #f0f2f5;
  padding: 1vh 1vw;
  border-radius: 0 1vh 1vh 1vh;
  color: #333;
  font-size: 0.8rem;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
}

.message-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1vh;
  flex: 1;
  min-width: 0; /* Fix flex child overflow */
}

.message-image img {
  max-width: 100%;
  max-height: 30vh;
  border-radius: 1vh;
  display: block;
  border: 1px solid rgba(0,0,0,0.05);
}


/* Markdown Styles */
:deep(.markdown-body) {
    font-size: 0.8rem;
    line-height: 1.6;
}
:deep(.markdown-body p) {
    margin-bottom: 0.5em;
}
:deep(.markdown-body p:last-child) {
    margin-bottom: 0;
}
:deep(.markdown-body ul), :deep(.markdown-body ol) {
    padding-left: 1.2em;
    margin-bottom: 0.5em;
}
:deep(.markdown-body li) {
    margin-bottom: 0.2em;
}
:deep(.markdown-body h1), :deep(.markdown-body h2), :deep(.markdown-body h3), :deep(.markdown-body h4) {
    margin-top: 1em;
    margin-bottom: 0.5em;
    font-weight: bold;
    line-height: 1.2;
}
:deep(.markdown-body code) {
    background-color: rgba(0,0,0,0.05);
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: monospace;
}
:deep(.markdown-body pre) {
    background-color: #2d2d2d;
    color: #fff;
    padding: 0.8em;
    border-radius: 5px;
    overflow-x: auto;
    margin-bottom: 0.8em;
}
:deep(.markdown-body blockquote) {
    margin: 0.5em 0;
    padding-left: 0.8em;
    border-left: 3px solid #d14d5e;
    color: #666;
}
:deep(.markdown-body a) {
    color: #d14d5e;
    text-decoration: none;
}
:deep(.markdown-body table) {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 0.8em;
}
:deep(.markdown-body th), :deep(.markdown-body td) {
    border: 1px solid #ddd;
    padding: 0.4em;
}
:deep(.markdown-body th) {
    background-color: #f8f9fa;
}

.user-message {
  align-self: flex-end;
  flex-direction: row;
  justify-content: flex-end;
}

.user-message .content {
  background: #d14d5e;
  color: #fff;
  border-radius: 1vh 1vh 0 1vh;
  padding: 1vh 1vw;
  font-size: 0.8rem;
  line-height: 1.4;
}

.user-avatar {
  width: 3.5vh;
  height: 3.5vh;
  background: #ccc;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
  color: #fff;
  flex-shrink: 0;
}

.chat-controls {
  height: 7vh;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid rgba(0,0,0,0.05);
  background: #fff;
  flex: 0 0 auto;
}

.voice-btn {
  width: 70%;
  height: 4vh;
  border-radius: 2vh;
  background: linear-gradient(135deg, #d14d5e 0%, #d65db1 100%);
  color: #fff;
  border: none;
  font-size: 0.9rem;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5vw;
  cursor: pointer;
  box-shadow: 0 0.5vh 1.5vh rgba(209, 77, 94, 0.4);
  transition: transform 0.1s;
}

.voice-btn .icon {
  font-size: 1rem;
}

.voice-btn:active {
  transform: scale(0.98);
}

.voice-btn.recording {
  background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); box-shadow: 0 0.5vh 1.5vh rgba(255, 77, 79, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0.5vh 2vh rgba(255, 77, 79, 0.6); }
  100% { transform: scale(1); box-shadow: 0 0.5vh 1.5vh rgba(255, 77, 79, 0.4); }
}

.confirm-controls {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1vh;
  padding: 0 1vw;
}

.recognized-text {
  font-size: 0.9rem;
  color: #333;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #f5f5f5;
  padding: 0.5vh 1vw;
  border-radius: 0.5vh;
}

.confirm-buttons {
  display: flex;
  gap: 2vw;
}

.cancel-btn, .confirm-btn {
  padding: 0.5vh 2vw;
  border-radius: 2vh;
  border: none;
  font-size: 0.9rem;
  cursor: pointer;
}

.cancel-btn {
  background: #f0f0f0;
  color: #666;
}

.confirm-btn {
  background: #d14d5e;
  color: #fff;
}

.recording-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2vh;
  color: #fff;
  backdrop-filter: blur(5px);
  border-radius: 1.5vh;
  pointer-events: none; /* Let events pass through to underlying elements */
}

.recording-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1vh;
  transition: all 0.3s;
}

.recording-status .icon {
  font-size: 3rem;
  background: #d14d5e;
  width: 10vh;
  height: 10vh;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 20px rgba(209, 77, 94, 0.6);
}

.recording-status.cancel-state .icon {
  background: #ff4d4f;
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(255, 77, 79, 0.6);
}

.recording-status .text {
  font-size: 1.2rem;
  font-weight: bold;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
}

.recording-status.cancel-state .text {
  color: #ff4d4f;
  background: rgba(255,255,255,0.9);
  padding: 0.5vh 1vw;
  border-radius: 0.5vh;
}

.realtime-text {
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  padding: 1.5vh 2vw;
  border-radius: 1vh;
  max-width: 85%;
  text-align: center;
  min-height: 4vh;
  font-size: 1rem;
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
}
</style>
